/**
 * The rule of a summoned panel: it closes with the panel that summoned it, and when the place
 * of the control that summoned it changes (`PanelConfig.summoner`).
 */

import type { OpenPanel, PanelId, PanelSummoner, PropWrite } from '@/types/panels';

/** Whether `props` moves `panel` off one of `keys`; a key with no value clears the prop. */
function movesAny(
    panel: OpenPanel, props: Record<string, unknown>, keys: readonly string[],
): boolean {
    return keys.some((key) => (
        Object.hasOwn(props, key) && (props[key] ?? undefined) !== (panel.props[key] ?? undefined)
    ));
}

/**
 * The open panels that leave with a departure: each panel whose summoner is in `departingIds`,
 * and each panel whose summoner `write` moves.
 */
export function getSummonedDependents(
    openPanels: OpenPanel[], departingIds: ReadonlySet<string>, write: PropWrite | null,
): ReadonlySet<PanelId> {
    const written = openPanels.find((panel) => panel.id === write?.id);
    const leaves = (summoner: PanelSummoner): boolean => (
        departingIds.has(summoner.id) || (write !== null && written?.id === summoner.id &&
            movesAny(written, write.props, summoner.props))
    );
    return new Set(openPanels
        .filter((panel) => (
            panel.config.summoner !== null && !departingIds.has(panel.id) &&
            leaves(panel.config.summoner)
        ))
        .map((panel) => panel.id));
}

/** The panels that a close of `id` closes: the panel and the panels it summoned. */
export function getClosingPanels(openPanels: OpenPanel[], id: PanelId): ReadonlySet<PanelId> {
    return new Set([id, ...getSummonedDependents(openPanels, new Set([id]), null)]);
}
